import { Box, IconButton } from "@mui/material";
import { FieldValue, OverviewStep } from "./OverviewStepSection.types.ts";
import { DotLegalHeader, DotLegalLink, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useTranslation } from "../../../localization/useTranslation.ts";
import NavigateToPageIcon from "../documentPreview/navigate-to-page-icon.svg?react";
import DynamicCellValueRenderer from "@common/components/dynamicCellRenderer/DynamicCellValueRenderer.tsx";
import { EntityType } from "@common/Common.types.ts";
import EntityRelationsTable from "@common/components/entityRelationsTable/EntityRelationsTable.tsx";
import OverviewRelationsField from "./OverviewRelationsField.tsx";
import { useOverviewStepSectionStyles } from "./OverviewStepSection.styles.ts";

export interface OverviewStepSectionProps {
    entityId: string;
    entityType: EntityType;
    step: OverviewStep;
    hideHeader?: boolean;
    getFieldDisplayName: (field: FieldValue, dictionary: Record<string, any>) => string;
    dictionary: Record<string, any>;
    navigateUrl?: string;
    canNavigate?: boolean;
    // Draws the relations as a field, with the names under a label, instead of the table. Only the incident log has switched so far.
    showRelationsAsField?: boolean;
}

function OverviewStepSection(props: OverviewStepSectionProps) {
    const { step } = props;

    const { translateString } = useTranslation();
    const styles = useOverviewStepSectionStyles();

    const fields = step.fields;
    const showRelationsField = step.showsRelations && props.showRelationsAsField;
    const showRelationsTable = step.showsRelations && !props.showRelationsAsField;
    const numberOfGridItems = fields.length + (showRelationsField ? 1 : 0);

    // Without a header or fields above it, the relations table would start at the top and slide under the navigate button in the corner.
    const getRelationsTopMargin = () => {
        if (fields.length > 0) {
            return 3;
        }

        return props.hideHeader ? 5 : 0;
    };

    const rows = {
        xs: numberOfGridItems,
        sm: Math.ceil(numberOfGridItems / 2),
        xl: Math.ceil(numberOfGridItems / 3),
    };

    return fields ? (
        <Box sx={{ position: "relative" }}>
            {!props.hideHeader && <DotLegalHeader headerStyle={"medium"}>{step.stepName}</DotLegalHeader>}
            <Box
                sx={{
                    display: "grid",
                    gap: 3,
                    gridAutoFlow: "column",
                    gridTemplateRows: {
                        xs: `repeat(${rows.xs}, auto)`,
                        sm: `repeat(${rows.sm}, auto)`,
                        xl: `repeat(${rows.xl}, auto)`,
                    },
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        xl: "1fr 1fr 1fr",
                    },
                }}
            >
                {fields.map((field, i) => (
                    <Box sx={{ alignItems: "flex-start" }} key={`${field.customFieldId ?? field.column}-${i}`}>
                        <Box sx={styles.fieldLabel}>{props.getFieldDisplayName(field, props.dictionary)}:</Box>

                        <Box sx={styles.fieldValue}>
                            <DynamicCellValueRenderer field={field} showFullContent />
                        </Box>
                    </Box>
                ))}
                {showRelationsField && <OverviewRelationsField entityId={props.entityId} />}
            </Box>
            {showRelationsTable && (
                <Box sx={{ mt: getRelationsTopMargin() }}>
                    <EntityRelationsTable entityId={props.entityId} entityType={props.entityType} canEdit={false} />
                </Box>
            )}
            {(props.navigateUrl || props.canNavigate === false) && (
                <Box sx={{ position: "absolute", top: -8, right: -8 }}>
                    <DotLegalTooltip
                        text={
                            props.canNavigate === false
                                ? translateString("roleDoesNotHaveAccessToFieldsOnStep")
                                : translateString("goToStepWithName", { name: step.stepName })
                        }
                    >
                        <Box component={"span"}>
                            {props.navigateUrl ? (
                                <DotLegalLink to={props.navigateUrl} stopPropagation>
                                    <IconButton>
                                        <NavigateToPageIcon height={22} width={22} />
                                    </IconButton>
                                </DotLegalLink>
                            ) : (
                                <IconButton disabled sx={{ opacity: 0.4 }}>
                                    <NavigateToPageIcon height={22} width={22} />
                                </IconButton>
                            )}
                        </Box>
                    </DotLegalTooltip>
                </Box>
            )}
        </Box>
    ) : undefined;
}

export default OverviewStepSection;
